import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  FlaskConical, Search, Loader2, RefreshCw, ChevronLeft, ChevronRight, Send,
} from 'lucide-react';
import { toast } from 'sonner';
import adminService from '../../services/adminService';

const EMPTY_CREDS = { testEmail: '', testPassword: '', additionalNotes: '' };

export default function AdminTrialRequestsPage() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pageSize: 25, totalPages: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isSendOpen, setIsSendOpen] = useState(false);
  const [creds, setCreds] = useState(EMPTY_CREDS);
  const [isSending, setIsSending] = useState(false);

  const fetchRequests = useCallback(async () => {
    setIsLoading(true);
    try {
      const filter = { page, pageSize: 25 };
      if (search) filter.search = search;
      if (statusFilter !== 'all') filter.status = statusFilter;
      const result = await adminService.getTrialRequests(filter);
      setData(result);
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to load trial requests');
    } finally {
      setIsLoading(false);
    }
  }, [search, statusFilter, page]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const openSendDialog = (request) => {
    setSelectedRequest(request);
    setCreds(EMPTY_CREDS);
    setIsSendOpen(true);
  };

  const handleSendCredentials = async () => {
    if (!creds.testEmail || !creds.testPassword) {
      toast.error('Test email and password are required.');
      return;
    }
    setIsSending(true);
    try {
      await adminService.sendTrialCredentials(selectedRequest.trialRequestId, {
        testEmail:       creds.testEmail,
        testPassword:    creds.testPassword,
        additionalNotes: creds.additionalNotes || undefined,
      });
      toast.success(`Credentials sent to ${selectedRequest.email}`);
      setIsSendOpen(false);
      fetchRequests();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to send credentials');
    } finally {
      setIsSending(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchRequests();
  };

  const statusBadge = (status) => {
    if (status === 'CredentialsSent')
      return <Badge className="bg-green-100 text-green-800">Credentials Sent</Badge>;
    return <Badge variant="outline">Pending</Badge>;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Trial Requests</h1>
            <p className="text-muted-foreground mt-1">Manage free trial enquiries from the home page</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchRequests}>
            <RefreshCw className="h-4 w-4 mr-2" />Refresh
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email or organization..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="CredentialsSent">Credentials Sent</SelectItem>
                </SelectContent>
              </Select>
              <Button type="submit">Search</Button>
            </form>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle>Trial Requests</CardTitle>
            <CardDescription>{data.total} total requests</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Organization</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Credentials Sent</TableHead>
                        <TableHead>Submitted</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.items?.map(req => (
                        <TableRow key={req.trialRequestId}>
                          <TableCell className="font-medium">{req.firstName} {req.lastName}</TableCell>
                          <TableCell className="text-muted-foreground">{req.email}</TableCell>
                          <TableCell className="text-muted-foreground">{req.phone ?? '—'}</TableCell>
                          <TableCell>{req.organization ?? '—'}</TableCell>
                          <TableCell>{statusBadge(req.status)}</TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {req.credentialsSentAt
                              ? new Date(req.credentialsSentAt).toLocaleString()
                              : '—'}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {new Date(req.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openSendDialog(req)}
                              title="Send credentials"
                            >
                              <Send className="h-4 w-4 text-blue-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {data.items?.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                            No trial requests found.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {data.page} of {data.totalPages} ({data.total} records)
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" disabled={page >= data.totalPages} onClick={() => setPage(p => p + 1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Send Credentials Dialog */}
      <Dialog open={isSendOpen} onOpenChange={setIsSendOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send Trial Credentials</DialogTitle>
            <DialogDescription>
              {selectedRequest?.firstName} {selectedRequest?.lastName} — {selectedRequest?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Test Account Email *</Label>
              <Input
                type="email"
                placeholder="trial@verdionix.com"
                value={creds.testEmail}
                onChange={e => setCreds(p => ({ ...p, testEmail: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Test Account Password *</Label>
              <Input
                type="text"
                placeholder="TrialPass@123"
                value={creds.testPassword}
                onChange={e => setCreds(p => ({ ...p, testPassword: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Additional Notes (optional)</Label>
              <Input
                placeholder="e.g. Your trial expires in 14 days."
                value={creds.additionalNotes}
                onChange={e => setCreds(p => ({ ...p, additionalNotes: e.target.value }))}
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button className="flex-1" onClick={handleSendCredentials} disabled={isSending}>
                {isSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Send Email
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => setIsSendOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
